import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOktaAuth } from '@okta/okta-react';
import {
  fetchCurrentUser,
  clearCurrentUser,
} from '../store/slices/authSlice';
import {
  getOktaAuth,
  oktaConfig,
  getPostLogoutRedirectUri,
} from '../config/auth';

/**
 * App-wide auth hook. Post-Okta-migration this is a thin adapter:
 *   - `isAuthenticated` reflects Okta's `authState.isAuthenticated`
 *   - `user` is the local Sequelize user row (id, username, email, role),
 *     loaded lazily via `GET /api/me` the first time we're authenticated
 *   - `login()` / `logout()` delegate to `oktaAuth`
 *
 * Consumers that used to read `user.role` / `user.id` / `user.username`
 * keep working without changes because the server's `/me` route returns
 * those fields in the same shape.
 */
export const useAuth = () => {
  const dispatch = useDispatch();
  const { authState } = useOktaAuth();
  const { user, loading, error, hasLoadedMe } = useSelector(
    (state) => state.auth
  );

  const isAuthResolved =
    authState != null && authState.isPending !== true;
  const isAuthenticated = Boolean(authState?.isAuthenticated);

  useEffect(() => {
    if (!isAuthResolved) return;
    if (
      isAuthenticated &&
      !user &&
      !loading &&
      !error &&
      !hasLoadedMe
    ) {
      dispatch(fetchCurrentUser());
    } else if (!isAuthenticated && user) {
      dispatch(clearCurrentUser());
    }
  }, [
    isAuthResolved,
    isAuthenticated,
    user,
    loading,
    error,
    hasLoadedMe,
    dispatch,
  ]);

  const login = () => {
    getOktaAuth().signInWithRedirect({ scopes: [...oktaConfig.scopes] });
  };

  const logout = async () => {
    dispatch(clearCurrentUser());
    await getOktaAuth().signOut({
      postLogoutRedirectUri: getPostLogoutRedirectUri(),
    });
  };

  return { user, isAuthenticated, loading, error, login, logout };
};
